import React, { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { BookOpenCheck, ShieldCheck, AlertTriangle, Upload, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../services/api";
import { Panel, thCls, tdCls, inputCls, cls, can, btnPrimary } from "../ui";

export default function CourtRules() {
  const { access } = useOutletContext();
  const isAdmin = can(access, "administration.manage");
  const [rows, setRows] = useState([]);
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [comp, setComp] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadRules = (query) =>
    api.get("/ujms/rules", { params: query ? { q: query } : {} }).then((r) => setRows(r.data)).catch(() => {});
  const loadOrders = () => api.get("/ujms/rules/orders").then((r) => setOrders(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/ujms/rules/compliance").then((r) => setComp(r.data)).catch(() => {});
    loadOrders();
  }, []);
  useEffect(() => {
    const t = setTimeout(() => loadRules(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/ujms/rules/upload", fd);
      toast.success(`${r.data.order}: ${r.data.added} provision(s) added${r.data.skipped ? `, ${r.data.skipped} already on file` : ""}`);
      loadRules(q); loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const removeOrder = async (o) => {
    if (!window.confirm(`Remove "${o.order}" (${o.count} provisions) from the rules library?`)) return;
    try {
      await api.delete(`/ujms/rules/orders/${o.order_id}`);
      toast.success(`${o.order} removed`);
      loadRules(q); loadOrders();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="court-rules-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2"><BookOpenCheck className="w-6 h-6" /> Court Rules & General Orders</h1>
          <p className="text-sm text-slate-500">General Orders on file — searchable here and informing the AI assistant's answers. Administrators can grow the library by uploading additional order PDFs.</p>
        </div>
        {isAdmin && (
          <div>
            <input ref={fileRef} type="file" accept=".pdf,application/pdf" className="hidden" onChange={onUpload} data-testid="rules-upload-input" />
            <button className={btnPrimary} disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="rules-upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? "Parsing with AI…" : "Upload General Order (PDF)"}
            </button>
          </div>
        )}
      </div>
      {orders.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="rules-orders-strip">
          {orders.map((o) => (
            <span key={o.order} className="inline-flex items-center gap-2 text-xs font-semibold bg-white border border-slate-300 rounded-sm px-2.5 py-1.5 text-slate-700"
              data-testid={`rules-order-chip-${(o.order || "").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
              {o.order} <span className="text-slate-400 font-normal">{o.count} provision(s)</span>
              {isAdmin && o.deletable && (
                <button onClick={() => removeOrder(o)} className="text-red-600 hover:text-red-800" aria-label={`Remove ${o.order}`}
                  data-testid={`rules-order-delete-${(o.order || "").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      {comp && (
        <Panel title="System Compliance with GO 1.2" testId="rules-compliance-panel">
          <div className="flex items-center gap-2 px-1 pb-3" data-testid="compliance-overall">
            {comp.overall === "COMPLIANT"
              ? <ShieldCheck className="w-5 h-5 text-emerald-600" />
              : <AlertTriangle className="w-5 h-5 text-amber-600" />}
            <span className={cls("text-sm font-semibold uppercase tracking-wide",
              comp.overall === "COMPLIANT" ? "text-emerald-700" : "text-amber-700")}>
              {comp.overall === "COMPLIANT" ? "System structure complies with General Order 1.2" : "Attention needed"}
            </span>
            <span className="text-xs text-slate-500">{comp.pass_count} pass · {comp.warn_count} warning(s)</span>
          </div>
          <table className="w-full border-collapse">
            <thead><tr><th className={thCls}>Rule</th><th className={thCls}>Requirement</th><th className={thCls}>Status</th><th className={thCls}>Detail</th></tr></thead>
            <tbody>
              {comp.items.map((it) => (
                <tr key={it.rule} className="hover:bg-slate-50 align-top" data-testid={`compliance-row-${it.rule.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                  <td className={`${tdCls} font-mono text-xs whitespace-nowrap font-semibold text-blue-900`}>{it.rule}</td>
                  <td className={`${tdCls} text-sm`}>{it.requirement}</td>
                  <td className={tdCls}>
                    <span className={cls("text-[11px] font-bold px-2 py-0.5 rounded-sm border",
                      it.status === "PASS" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200")}>
                      {it.status}
                    </span>
                  </td>
                  <td className={`${tdCls} text-sm text-slate-600`}>{it.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
      <input className={`${inputCls} max-w-md`} value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Search rules… e.g. Deputy Chief Judge, Municipal" aria-label="Search court rules" data-testid="rules-search" />
      <Panel title={`Rules Library — ${rows.length} provision(s)`} testId="rules-panel">
        <table className="w-full border-collapse">
          <thead><tr><th className={thCls}>Section</th><th className={thCls}>Title</th><th className={thCls}>Summary</th><th className={thCls}>Order</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.section} className="hover:bg-slate-50 align-top" data-testid={`rule-row-${r.section.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                <td className={`${tdCls} font-mono text-xs whitespace-nowrap font-semibold text-blue-900`}>{r.section}</td>
                <td className={`${tdCls} font-medium`}>{r.title}</td>
                <td className={`${tdCls} text-sm text-slate-600`}>{r.summary}</td>
                <td className={`${tdCls} text-xs text-slate-500 whitespace-nowrap`}>{r.order || r.source}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={4} className={`${tdCls} text-slate-400`}>No rules match.</td></tr>}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}
